/**
 * useRecovery Hook - Transfer Recovery Management
 *
 * Manages recovery of pending/incomplete transfers:
 * 1. Checks localStorage for burns that didn't complete
 * 2. Queries backend for current status of those transfers
 * 3. Provides list of pending transfers for UI display
 * 4. Allows user to resume tracking or retry submission
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { bridgeApi, walletApi, intentApi } from '@/services/api'
import type { Transfer, TransferStatus, Chain } from '@/types'
import { getDisplayError } from '@/utils/errors'
import {
  getStoredBurns,
  getBurnsForAddress,
  getBurnsNeedingRecovery,
  updateBackendStatus,
  removeBurn,
  createPendingBurn,
  markBurnConfirmed,
  cleanupOldTerminalTransfers,
  type StoredBurn,
} from '@/utils/burnStorage'

// Terminal statuses - no longer need tracking
const TERMINAL_STATUSES: TransferStatus[] = ['mint_success', 'recovered', 'failed']

export interface PendingTransfer extends StoredBurn {
  backendStatus?: TransferStatus
  backendTransfer?: Transfer
  needsRecovery: boolean
}

interface UseRecoveryReturn {
  pendingTransfers: PendingTransfer[]
  isLoading: boolean
  error: string | null
  checkWalletAndRecover: (address: string) => Promise<void>
  checkForPendingTransfers: (address?: string) => Promise<void>
  checkWalletHistory: (address: string) => Promise<void>
  checkBlockExplorer: (address: string) => Promise<number>
  resumeTransfer: (burnTxHash: string) => void
  dismissTransfer: (id: string) => void
  clearAll: () => void
}

export function useRecovery(): UseRecoveryReturn {
  const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track if we've done initial check
  const initialCheckDone = useRef(false)

  /**
   * Check backend for status of a single burn
   */
  const checkBurnStatus = useCallback(async (burn: StoredBurn): Promise<PendingTransfer> => {
    // If no burnTxHash yet, it's still pending initial burn
    if (!burn.burnTxHash) {
      return {
        ...burn,
        needsRecovery: burn.pendingStatus === 'pending', // Stale pending burns need attention
      }
    }

    try {
      const response = await bridgeApi.getTransferStatus(burn.burnTxHash)

      if (response.success && response.data) {
        const backendTransfer = response.data
        const backendStatus = backendTransfer.status

        // Update localStorage with backend status
        updateBackendStatus(burn.id, backendStatus)

        // If terminal, it doesn't need recovery
        const needsRecovery = !TERMINAL_STATUSES.includes(backendStatus)

        return {
          ...burn,
          backendStatus,
          backendTransfer,
          needsRecovery,
        }
      }

      // Backend doesn't know about this transfer - needs recovery
      // This could happen if submission failed
      return {
        ...burn,
        needsRecovery: true,
      }
    } catch (e) {
      console.warn(`Failed to check status for ${burn.burnTxHash}:`, e)
      return {
        ...burn,
        needsRecovery: true,
      }
    }
  }, [])

  /**
   * Check for all pending transfers
   * Optionally filter by address
   */
  const checkForPendingTransfers = useCallback(async (address?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Get burns from localStorage - either by address or all needing recovery
      const storedBurns = address
        ? getBurnsForAddress(address)
        : getBurnsNeedingRecovery()

      // Also include any burns with confirmed burn tx that might need checking
      const allBurns = address ? storedBurns : getStoredBurns()
      const burnsToCheck = allBurns.filter(b =>
        b.pendingStatus === 'burned' ||
        b.pendingStatus === 'submission_failed' ||
        (b.pendingStatus === 'submitted' && b.burnTxHash) // Re-check submitted ones too
      )

      if (burnsToCheck.length === 0) {
        // Don't clear existing transfers - just return if nothing new to check
        // This preserves transfers from other wallets
        return
      }

      console.log(`🔍 Checking ${burnsToCheck.length} stored burns for recovery...`)

      // Check each burn against backend
      const results = await Promise.all(burnsToCheck.map(checkBurnStatus))

      // Show ALL transfers in the list (including completed ones)
      // Completed transfers stay visible until user dismisses them
      // This gives users visibility into their transfer history
      setPendingTransfers(results)

      // Log how many are still pending (not completed yet)
      const stillPending = results.filter(t => t.needsRecovery)
      if (stillPending.length > 0) {
        console.log(`⚠️ Found ${stillPending.length} pending transfers that may need attention`)
      }
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Failed to check pending transfers'
      setError(getDisplayError(errorMsg))
      console.error('Recovery check failed:', e)
    } finally {
      setIsLoading(false)
    }
  }, [checkBurnStatus])

  /**
   * Resume tracking a specific transfer
   * Starts polling but keeps transfer visible in pending list
   * Transfer is only removed when it completes or user dismisses it
   */
  const resumeTransfer = useCallback((burnTxHash: string) => {
    console.log(`🔄 Resuming transfer: ${burnTxHash.slice(0, 10)}...`)
    // The actual polling will be handled by the caller (useBridge/useMint)
    // Keep in pending list - it will be removed when status reaches terminal state
    // or when user manually dismisses it
  }, [])

  /**
   * Dismiss a transfer (user acknowledges it)
   * Only works for terminal states (mint_success or failed)
   * Removes from localStorage using the ID
   */
  const dismissTransfer = useCallback((id: string) => {
    // Find the transfer to check if it's dismissable
    const transfer = pendingTransfers.find(t => t.id === id)
    if (transfer && !TERMINAL_STATUSES.includes(transfer.backendStatus as TransferStatus)) {
      console.warn(`⚠️ Cannot dismiss non-terminal transfer: ${id}`)
      return
    }

    removeBurn(id)
    setPendingTransfers(prev => prev.filter(t => t.id !== id))
    console.log(`🗑️ Dismissed transfer: ${id}`)
  }, [pendingTransfers])

  /**
   * Clear all terminal (completed/failed) transfers
   * Only dismisses transfers in terminal state - pending transfers remain
   */
  const clearAll = useCallback(() => {
    const terminalTransfers = pendingTransfers.filter(t =>
      TERMINAL_STATUSES.includes(t.backendStatus as TransferStatus)
    )
    terminalTransfers.forEach(t => removeBurn(t.id))
    setPendingTransfers(prev => prev.filter(t =>
      !TERMINAL_STATUSES.includes(t.backendStatus as TransferStatus)
    ))
    console.log(`🗑️ Cleared ${terminalTransfers.length} completed transfers`)
  }, [pendingTransfers])

  /**
   * @deprecated No longer used - was re-adding dismissed transfers from backend history.
   * Recovery now relies solely on intent layer + block explorer fallback.
   * Kept for potential future use but not called in recovery flow.
   *
   * Wallet fallback: Check transfer history for any non-terminal transfers
   * that might not be in localStorage (e.g., different browser/device)
   *
   * This uses the backend's transfer history endpoint which returns
   * transfers associated with an address.
   */
  const checkWalletHistory = useCallback(async (address: string) => {
    try {
      console.log(`🔍 Checking wallet history for ${address.slice(0, 10)}...`)

      const response = await bridgeApi.getTransferHistory(address)

      if (!response.success || !response.data) {
        console.log('No transfer history found')
        return
      }

      const transfers = response.data

      // Find non-terminal transfers that aren't in localStorage
      const storedBurns = getStoredBurns()
      const storedTxHashes = new Set(storedBurns.map(b => b.burnTxHash).filter(Boolean))

      const missingTransfers = transfers.filter(t =>
        !TERMINAL_STATUSES.includes(t.status) &&
        t.burnTxHash &&
        !storedTxHashes.has(t.burnTxHash)
      )

      if (missingTransfers.length === 0) {
        console.log('No missing transfers found in wallet history')
        return
      }

      console.log(`⚠️ Found ${missingTransfers.length} transfers in history not in localStorage`)

      // Create localStorage entries for these transfers so they show in recovery UI
      for (const transfer of missingTransfers) {
        const pendingBurn = createPendingBurn({
          sourceChain: transfer.sourceChain as Chain,
          destinationChain: transfer.destinationChain as Chain,
          sourceAddress: transfer.sourceAddress,
          destinationAddress: transfer.destinationAddress,
          amount: transfer.amount,
        })

        // Mark it as confirmed with the tx hash
        if (transfer.burnTxHash) {
          markBurnConfirmed(pendingBurn.id, transfer.burnTxHash)
        }

        // Update with backend status
        updateBackendStatus(pendingBurn.id, transfer.status)

        console.log(`📝 Added missing transfer to localStorage: ${transfer.burnTxHash?.slice(0, 10)}...`)
      }

      // Refresh the pending transfers list
      await checkForPendingTransfers(address)
    } catch (e) {
      console.warn('Failed to check wallet history:', e)
    }
  }, [checkForPendingTransfers])

  /**
   * Block explorer fallback: Check on-chain for burns not in our database
   * Used when localStorage is wiped and backend doesn't have the transfer
   * Auto-submits found burns to backend and starts polling
   *
   * @param address - Wallet address to check
   * @param pendingIntents - Optional list of pending intents to match burns against
   */
  const checkBlockExplorer = useCallback(async (
    address: string,
    pendingIntents?: Array<{ id: string; source_chain: string; destination_chain: string; destination_address: string; amount: string; created_at: number }>
  ): Promise<number> => {
    try {
      console.log(`🔍 Checking block explorer for ${address.slice(0, 10)}...`)

      const response = await walletApi.checkBurns(address)

      if (!response.success || !response.data) {
        console.warn('Block explorer check failed:', response.error?.message)
        return 0
      }

      const { burns } = response.data

      if (burns.length === 0) {
        console.log('   No unknown burns found on-chain')
        return 0
      }

      console.log(`⚠️ Found ${burns.length} unknown burn(s) on-chain, auto-submitting...`)

      let submitted = 0

      for (const burn of burns) {
        try {
          const sourceChain = burn.chain

          // Try to match this burn to a pending intent
          // Match by: source chain matches AND burn timestamp is close to intent creation time
          type IntentType = { id: string; source_chain: string; destination_chain: string; destination_address: string; amount: string; created_at: number }
          let matchedIntent: IntentType | undefined
          if (pendingIntents && pendingIntents.length > 0) {
            matchedIntent = pendingIntents.find((intent): intent is IntentType => {
              const chainMatch = intent.source_chain === sourceChain
              // Allow 1 hour window for matching (burn could be before or after intent due to timing)
              const timeDiff = Math.abs(burn.timestamp - intent.created_at)
              const timeMatch = timeDiff < 60 * 60 * 1000 // 1 hour
              return chainMatch && timeMatch
            })

            if (matchedIntent) {
              console.log(`   📋 Matched burn to intent ${matchedIntent.id.slice(0, 8)}... (${matchedIntent.source_chain} → ${matchedIntent.destination_chain})`)
            }
          }

          // Use matched intent data or fall back to heuristic
          const destinationChain = matchedIntent?.destination_chain ||
            (sourceChain === 'solana' ? 'base' : sourceChain === 'base' ? 'ethereum' : 'base')
          const destinationAddress = matchedIntent?.destination_address || ''
          const amount = matchedIntent?.amount || '0'

          // Auto-submit to backend
          const submitResponse = await bridgeApi.initiateHybridTransfer({
            amount,
            sourceChain: sourceChain as Chain,
            destinationChain: destinationChain as Chain,
            sourceAddress: burn.from,
            destinationAddress,
            burnTxHash: burn.txHash,
          })

          if (submitResponse.success) {
            console.log(`   ✅ Auto-submitted burn: ${burn.txHash.slice(0, 10)}...`)

            // Create localStorage entry for this burn
            const pendingBurn = createPendingBurn({
              sourceChain: burn.chain as Chain,
              destinationChain: destinationChain as Chain,
              sourceAddress: burn.from,
              destinationAddress,
              amount,
            })

            markBurnConfirmed(pendingBurn.id, burn.txHash)
            submitted++
          } else {
            console.warn(`   ⚠️ Failed to submit burn: ${submitResponse.error?.message}`)
          }
        } catch (submitError) {
          console.warn(`   ⚠️ Error submitting burn ${burn.txHash.slice(0, 10)}:`, submitError)
        }
      }

      // Refresh pending transfers list if any were submitted
      if (submitted > 0) {
        await checkForPendingTransfers(address)
      }

      return submitted
    } catch (e) {
      console.warn('Error checking block explorer:', e)
      return 0
    }
  }, [checkForPendingTransfers])

  /**
   * Recovery check for a connected wallet.
   *
   * NO LONGER GATED ON A `wallets` ROW EXISTING (#478-W section B / H1 remedy (d)).
   *
   * This function used to call GET /api/wallet/check first and RETURN EARLY when it answered
   * `exists: false`. That gate was written when POST /wallet/track created a `wallets` row for
   * every transfer attempt, so 'no row' was a safe proxy for 'no history worth checking'.
   * #478-W P3+ removed that row creation, which would have turned the gate into a silent
   * DENIAL OF THE RECOVERY FLOW for a real user who has a recorded intent but no wallets row
   * (invariant I9: no user with a recorded burn may be denied recovery).
   *
   * WHY DELETING THE GATE IS SAFE RATHER THAN MERELY NECESSARY. `useBridge.ts` writes the
   * intents row PRE-BURN ('Step -1: Save intent to DB BEFORE wallet popup') and ABORTS the
   * bridge if that write fails, so an intents row exists for every burn this app can perform -
   * `executeBurn` has exactly one call site. The recovery data therefore survives P3+ untouched;
   * only the gate stopped us reaching it. The comment below already said the design is to rely
   * solely on the intent layer; the `exists` gate was what stopped it doing so.
   *
   * The alternatives (key the gate on a `transfers` row, or broaden what /check reports) were
   * both rejected: those artifacts are written POST-burn, so neither covers
   * burn-happened-submit-failed, which is the case where recovery actually matters.
   *
   * Adds no new server surface: `intentApi.checkIntents` was already reachable under the same
   * generalLimiter + requirePublicKey() gate as the /check call it replaces. The cost is one
   * extra intents lookup for a genuinely new wallet, which returns two empty arrays.
   *
   * IMPORTANT: This function uses the intent API (checkIntents) to get authoritative
   * data from the database, fixing the stale closure bug where pendingTransfers.length
   * would use stale React state after async operations.
   */
  const checkWalletAndRecover = useCallback(async (address: string) => {
    try {
      console.log(`🔍 Running recovery check: ${address.slice(0, 10)}...`)

      // Check localStorage first
      await checkForPendingTransfers(address)

      // NOTE: checkWalletHistory removed - it was re-adding dismissed transfers
      // from backend history. We now rely solely on intent layer for recovery.
      // Intent layer handles the browser wipe scenario without re-adding dismissed transfers.

      // Check the intents table for authoritative state (was 'Step 2' when a /wallet/check
      // existence probe was Step 1 -- that step is gone, see the docblock).
      // This replaces the stale pendingTransfers.length check
      console.log('📋 Checking intent database for pending/stale intents...')
      const intentResponse = await intentApi.checkIntents(address)

      if (!intentResponse.success) {
        console.warn('Failed to check intents:', intentResponse.error?.message)
        return
      }

      const { intents, staleIntents } = intentResponse.data || {
        intents: [],
        staleIntents: [],
      }

      console.log(`📋 Found ${intents.length} pending intents, ${staleIntents.length} stale`)

      // If we have burned or submitted intents, ensure they're tracked locally
      const burnedIntents = intents.filter(i => i.status === 'burned')
      const submittedIntents = intents.filter(i => i.status === 'submitted')

      if (burnedIntents.length > 0) {
        console.log(`🔥 Found ${burnedIntents.length} burned intents awaiting submission`)
      }

      if (submittedIntents.length > 0) {
        console.log(`📤 Found ${submittedIntents.length} submitted intents, checking if any need recovery...`)

        // For submitted intents, only re-add to localStorage if:
        // 1. Not already in localStorage
        // 2. Transfer is NOT in terminal state (still pending)
        // This prevents re-adding dismissed completed transfers
        const storedBurns = getStoredBurns()
        const storedTxHashes = new Set(storedBurns.map(b => b.burnTxHash).filter(Boolean))

        let addedCount = 0
        for (const intent of submittedIntents) {
          if (intent.burn_tx_hash && !storedTxHashes.has(intent.burn_tx_hash)) {
            // Check backend status before re-adding
            try {
              const statusResponse = await bridgeApi.getTransferStatus(intent.burn_tx_hash)
              if (statusResponse.success && statusResponse.data) {
                const backendStatus = statusResponse.data.status
                // Only re-add if NOT in terminal state
                if (backendStatus !== 'mint_success' && backendStatus !== 'failed' && backendStatus !== 'recovered') {
                  console.log(`📝 Re-creating localStorage entry for pending intent: ${intent.id.slice(0, 8)}... (status: ${backendStatus})`)

                  // Create localStorage entry from intent data
                  const pendingBurn = createPendingBurn({
                    sourceChain: intent.source_chain as Chain,
                    destinationChain: intent.destination_chain as Chain,
                    sourceAddress: intent.source_address,
                    destinationAddress: intent.destination_address,
                    amount: intent.amount,
                  })

                  // Mark it as confirmed with the tx hash
                  markBurnConfirmed(pendingBurn.id, intent.burn_tx_hash)
                  addedCount++
                } else {
                  console.log(`   Skipping completed intent: ${intent.id.slice(0, 8)}... (status: ${backendStatus})`)
                }
              }
            } catch (e) {
              console.warn(`   Failed to check status for intent ${intent.id.slice(0, 8)}:`, e)
            }
          }
        }

        // Refresh the pending transfers list if any were added
        if (addedCount > 0) {
          await checkForPendingTransfers(address)
        }
      }

      // Check block explorer for ANY pending intents (not just stale ones)
      // This handles the case where user refreshed the page before burn confirmed,
      // then approved the burn in the wallet popup that was still open
      const pendingIntentsOnly = intents.filter(i => i.status === 'pending')
      if (pendingIntentsOnly.length > 0) {
        console.log(`📡 Found ${pendingIntentsOnly.length} pending intent(s), checking block explorer for orphaned burns...`)
        // Pass pending intents so we can match burns to their correct destinations
        const foundBurns = await checkBlockExplorer(address, pendingIntentsOnly)

        if (foundBurns > 0) {
          console.log(`✅ Found ${foundBurns} burns on-chain from pending intents`)
          // The checkBlockExplorer function auto-submits found burns
        }
      }

    } catch (e) {
      console.warn('Error in checkWalletAndRecover:', e)
      // Fall back to localStorage check on error
      await checkForPendingTransfers(address)
    }
  }, [checkForPendingTransfers, checkBlockExplorer])

  // Initial check on mount
  useEffect(() => {
    if (!initialCheckDone.current) {
      initialCheckDone.current = true
      // Cleanup old terminal transfers (30+ days) before loading
      cleanupOldTerminalTransfers()
      checkForPendingTransfers()
    }
  }, [checkForPendingTransfers])

  return {
    pendingTransfers,
    isLoading,
    error,
    checkWalletAndRecover,
    checkForPendingTransfers,
    checkWalletHistory,
    checkBlockExplorer,
    resumeTransfer,
    dismissTransfer,
    clearAll,
  }
}
